import React from "react";
import { Box, Group, Select, Title, UnstyledButton } from "@mantine/core";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { CalendarViewType } from "../../../../../types/calendar";
import { generateBookingLink } from "../../../../../utils/bookingLinkUtils";
import { DESIGN_ICONS } from "../../../../../theme/designIcons";
import { CopyLinkIcon } from "../../../../common/CopyLinkIcon";
import styles from "./Header.module.css";

interface HeaderProps {
  /** True while the booking drawer is open. Restricts the view switcher to
   *  Day/Week (List/Card make no sense mid-booking). */
  schedulingOpen?: boolean;
  /** True while any calendar drawer is open: the tabs shift beside it.
   *  Defaults to `schedulingOpen`. */
  drawerOpen?: boolean;
  /** True while a full-screen scheduling surface (Reschedule / Rebook) is
   *  focused — hides the salon name + booking link. */
  focusMode?: boolean;
  /** Walk-Ins button (right of the tabs); omitted = hidden. */
  walkIns?: HeaderWalkIns;
}

export interface HeaderWalkIns {
  /** Waiting walk-ins (badge hidden at 0). */
  count: number;
  /** The walk-ins drawer is showing (pressed look). */
  active: boolean;
  onToggle: () => void;
}

/** View tabs, in order: label + trailing icon (design handoff header). */
const VIEW_TABS: {
  value: CalendarViewType;
  label: string;
  Icon: (typeof DESIGN_ICONS)[keyof typeof DESIGN_ICONS];
}[] = [
  { value: "day", label: "Day", Icon: DESIGN_ICONS.Calendar },
  { value: "week", label: "Week", Icon: DESIGN_ICONS.CalendarDays },
  { value: "list", label: "List", Icon: DESIGN_ICONS.List },
  { value: "card", label: "Card", Icon: DESIGN_ICONS.LayoutGrid },
];

const WalkInsIcon = DESIGN_ICONS.Footprints;

/**
 * Calendar top bar: salon name + copy-booking-link on the left, the bordered
 * Day / Week / List / Card tabs and the Walk-Ins button on the right
 * (mobile: a view Select).
 */
const Header: React.FC<HeaderProps> = ({
  schedulingOpen = false,
  drawerOpen = schedulingOpen,
  focusMode = false,
  walkIns,
}) => {
  const { viewType, setViewType } = useCalendar();

  const { businessInfo } = useSalon() as {
    businessInfo: { id: string; salon_name: string } | null;
  };

  const { isMobile } = useResponsive();

  const bookingLink = generateBookingLink(businessInfo?.id);

  // While the booking drawer is open, only Day/Week are offered — List/Card
  // aren't schedulable surfaces.
  const isSchedulingView = (o: { value: string }) =>
    !schedulingOpen || o.value === "day" || o.value === "week";
  const shownTabs = VIEW_TABS.filter(isSchedulingView);

  return (
    <Box className={styles.header}>
      <Group
        gap={4}
        wrap="nowrap"
        className={styles.salonInfo}
        // Full-screen scheduling focus (Reschedule / Rebook) hides the salon
        // name + booking link so the edit surface reads as its own screen.
        hidden={focusMode}
      >
        <Title
          order={isMobile ? 4 : 2}
          className={styles.salonName}
          fz={isMobile ? undefined : 22}
          fw={600}
        >
          {businessInfo?.salon_name || "Loading..."}
        </Title>
        <CopyLinkIcon
          value={bookingLink}
          label="Copy booking link"
          className={styles.linkButton}
        />
      </Group>

      {isMobile ? (
        <Select
          value={viewType}
          onChange={(value) => setViewType(value as CalendarViewType)}
          data={shownTabs.map(({ value, label }) => ({ value, label }))}
          size="xs"
          rightSection={<DESIGN_ICONS.ChevronDown size={14} />}
          className={styles.viewDropdown}
          styles={{
            input: {
              width: "100px",
              fontWeight: 500,
            },
          }}
        />
      ) : (
        // While the booking drawer is open it covers the right of the
        // viewport, so the tabs shift left by the drawer width to stay
        // visible beside (not under) it.
        <Box
          className={`${styles.rightSection} ${drawerOpen ? styles.rightSectionShifted : ""}`}
        >
          <Box
            className={styles.viewTabs}
            role="tablist"
            aria-label="Calendar view"
          >
            {shownTabs.map(({ value, label, Icon }) => {
              const active = viewType === value;
              return (
                <UnstyledButton
                  key={value}
                  role="tab"
                  aria-selected={active}
                  data-active={active || undefined}
                  className={styles.viewTab}
                  onClick={() => setViewType(value)}
                >
                  {label}
                  <Icon size={15} />
                </UnstyledButton>
              );
            })}
          </Box>
          {walkIns && (
            <UnstyledButton
              className={styles.walkInsBtn}
              data-active={walkIns.active || undefined}
              aria-pressed={walkIns.active}
              onClick={walkIns.onToggle}
            >
              Walk-Ins
              <WalkInsIcon size={15} />
              {walkIns.count > 0 && (
                <span
                  className={styles.walkInsBadge}
                  aria-label={`${walkIns.count} waiting`}
                >
                  {walkIns.count}
                </span>
              )}
            </UnstyledButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Header;
